import React, { useState } from "react";
import { Copy } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { mediaUrl } from "../../../lib/cms";
import { PageTitle, Loading } from "../ui";
import { useFlash, errText, Modal, bytes } from "./cmsShared";
import { useMedia, UploadButton, MediaTile } from "./MediaGrid";

export default function MediaLibrary() {
  const { user } = useAuth();
  const { items, load } = useMedia();
  const [kind, setKind] = useState("all");
  const [sel, setSel] = useState(null);
  const [flashNode, flash] = useFlash();

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.filename} from the library?`)) return;
    try { await api.delete(`/cms/media/${m.id}`); load(); flash("Asset removed."); } catch (e) { flash(errText(e), "error"); }
  };
  const canDelete = (m) => user?.cms?.admin || m.uploaded_by === user?.email;
  if (!items) return <Loading />;
  const list = items.filter((m) => kind === "all" || m.kind === kind);

  return (
    <div data-testid="media-library">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle eyebrow="Content · Assets" title="Media Library" sub="Images, logos and files for use across public content. Up to 15 MB each — assets become public only once used in published content." />
        {user?.cms?.author && <UploadButton onUploaded={() => { load(); flash("Uploaded."); }} onError={(e) => flash(e, "error")} />}
      </div>
      <div className="mb-6 flex gap-2">
        {["all", "image", "file"].map((k) => (
          <button key={k} onClick={() => setKind(k)}
            className={`rounded-full px-4 py-1.5 font-sans text-xs font-semibold uppercase tracking-widest transition-colors ${kind === k ? "bg-gold text-sapphire-deep" : "bg-white text-sapphire-deep/70 ring-1 ring-sapphire-deep/10"}`}
            data-testid={`media-filter-${k}`}>{k === "all" ? "All" : k === "image" ? "Images" : "Files"}</button>
        ))}
      </div>
      {list.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-sapphire-deep/20 py-16 text-center font-sans text-sm text-stonegrey" data-testid="media-empty">No assets yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 xl:grid-cols-5">
          {list.map((m) => <MediaTile key={m.id} m={m} onClick={() => setSel(m)} onDelete={canDelete(m) ? del : null} />)}
        </div>
      )}
      {sel && (
        <Modal title={sel.filename} onClose={() => setSel(null)} testid="media-detail">
          {sel.kind === "image" && <img src={mediaUrl(sel.id)} alt="" className="mb-4 max-h-80 w-full rounded-xl object-contain bg-parchment" />}
          <dl className="grid grid-cols-2 gap-3 font-sans text-sm">
            <dt className="text-stonegrey">Type</dt><dd>{sel.content_type}</dd>
            <dt className="text-stonegrey">Size</dt><dd>{bytes(sel.size)}</dd>
            <dt className="text-stonegrey">Uploaded by</dt><dd>{sel.uploaded_by_name || sel.uploaded_by}</dd>
            <dt className="text-stonegrey">Visibility</dt><dd>{sel.public ? "Public (in published content)" : "Private"}</dd>
          </dl>
          <button onClick={() => { navigator.clipboard?.writeText(mediaUrl(sel.id)); flash("Link copied."); }} className="btn-dark mt-5" data-testid="media-copy-link"><Copy size={14} /> Copy link</button>
        </Modal>
      )}
      {flashNode}
    </div>
  );
}
